import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import {
  useGetPlatformDevices,
  useGetPlatformLocations,
  useGetPlatformOrganisations,
  useGetPlatformUsers,
  useSetPlatformUserStatus,
} from "../../queries/platform";
import { PlatformDevice, PlatformUser } from "../../api/platform";
import { useGetDeviceParameters } from "../../queries/parameters";
import { ParameterData } from "../../types/device";
import ConfigurationWidget from "../../widgets/configuration/ConfigurationWidget";
import MqttPublishWidget from "../../widgets/configuration/MqttPublishWidget";

type Tab = "Users" | "Organisations" | "Locations" | "Devices" | "MQTT";

const TABS: { key: Tab; icon: string }[] = [
  { key: "Users", icon: "mdi:account-group-outline" },
  { key: "Organisations", icon: "mdi:domain" },
  { key: "Locations", icon: "mdi:map-marker-outline" },
  { key: "Devices", icon: "mdi:router-wireless" },
  { key: "MQTT", icon: "mdi:server-network" },
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

function StatusBadge({ deactivated }: { deactivated: boolean }) {
  return deactivated ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Inactive
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Active
    </span>
  );
}

// Case-insensitive match across the searchable text of a row
const matches = (search: string, ...fields: (string | null | undefined)[]) =>
  !search ||
  fields.some((f) => f && f.toLowerCase().includes(search.toLowerCase()));

function UsersTab({ search }: { search: string }) {
  const { data, isLoading } = useGetPlatformUsers();
  const { mutate: setStatus, isPending } = useSetPlatformUserStatus();
  const [confirmUser, setConfirmUser] = useState<PlatformUser | null>(null);

  const users = (data?.data || []).filter((u) =>
    matches(search, u.name, u.email, u.client?.name, u.role),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {["Name", "Email", "Organisation", "Role", "Created", "Status", "Action"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {u.name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {u.email}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {u.client?.name || "—"}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{u.role}</td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {formatDate(u.createdAt)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge deactivated={u.deactivated} />
                </td>
                <td className="px-4 py-3">
                  <button
                    disabled={isPending}
                    onClick={() => setConfirmUser(u)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
                      u.deactivated
                        ? "border-green-300 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20"
                        : "border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                    }`}
                  >
                    {u.deactivated ? "Activate" : "Deactivate"}
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm status change */}
      {confirmUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {confirmUser.deactivated ? "Activate user?" : "Deactivate user?"}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {confirmUser.deactivated ? (
                <>
                  <strong>{confirmUser.name}</strong> ({confirmUser.email}) will regain access to
                  the platform.
                </>
              ) : (
                <>
                  <strong>{confirmUser.name}</strong> ({confirmUser.email}) will be logged out and
                  blocked from using the platform until reactivated.
                </>
              )}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setConfirmUser(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setStatus({ userId: confirmUser.id, deactivated: !confirmUser.deactivated });
                  setConfirmUser(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
                  confirmUser.deactivated
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {confirmUser.deactivated ? "Activate" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function OrganisationsTab({ search }: { search: string }) {
  const { data, isLoading } = useGetPlatformOrganisations();
  const organisations = (data?.data || []).filter((o) =>
    matches(search, o.name, o.email, o.phone, o.website),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {["Name", "Email", "Phone", "Users", "Locations", "Devices", "Created"].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
          {organisations.map((o) => (
            <tr key={o.id}>
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                {o.name}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {o.email || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{o.phone || "—"}</td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{o._count.users}</td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {o._count.locations}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{o._count.devices}</td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {formatDate(o.createdAt)}
              </td>
            </tr>
          ))}
          {organisations.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                No organisations found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function LocationsTab({ search }: { search: string }) {
  const { data, isLoading } = useGetPlatformLocations();
  const locations = (data?.data || []).filter((l) =>
    matches(search, l.name, l.address, l.client?.name),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {["Name", "Organisation", "Address", "Devices", "Created"].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
          {locations.map((l) => (
            <tr key={l.id}>
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                {l.name}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {l.client?.name || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {l.address || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {l._count.devices}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {formatDate(l.createdAt)}
              </td>
            </tr>
          ))}
          {locations.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                No locations found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DevicesTab({ search }: { search: string }) {
  const { data, isLoading } = useGetPlatformDevices();
  const devices = (data?.data || []).filter((d) =>
    matches(search, d.name, d.identifier, d.modelType, d.client?.name, d.location?.name),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {["Name", "Identifier", "Model", "Organisation", "Location", "Created"].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
          {devices.map((d) => (
            <tr key={d.id}>
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                {d.name}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {d.identifier || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                {d.modelType || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {d.client?.name || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {d.location?.name || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {formatDate(d.createdAt)}
              </td>
            </tr>
          ))}
          {devices.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                No devices found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// One MQTT tab with a shared device-ID selector at the top. The chosen device
// drives both the connection settings (topic) and the publish model below.
function MqttTab() {
  const { data, isLoading } = useGetPlatformDevices();
  const devices = data?.data || [];

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PlatformDevice | null>(null);

  const filtered = devices.filter((d) =>
    matches(query, d.name, d.identifier, d.id, d.client?.name, d.location?.name),
  );

  const { data: rawParams, isLoading: paramsLoading } = useGetDeviceParameters(
    selected?.id,
  );

  // The publish widget wants ParameterData; the model endpoint returns the bare
  // parameter definitions, so fill in the UI-only fields it doesn't provide.
  const parameters: ParameterData[] = useMemo(
    () =>
      (rawParams || []).map((p) => ({
        ...p,
        status: "normal" as const,
        trend: "stable" as const,
        trendValue: 0,
      })),
    [rawParams],
  );

  const selectDevice = (d: PlatformDevice) => {
    setSelected(d);
    setQuery(d.id);
    setOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Shared device selector */}
      <div className="max-w-xl">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Device ID
        </label>
        <div className="relative">
          <Icon
            icon="mdi:magnify"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="72100594-6452-4961-812a-9a9b4c216bf7"
            className="w-full pl-9 pr-8 py-2 text-sm font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {(query || selected) && (
            <button
              onClick={() => {
                setQuery("");
                setSelected(null);
                setOpen(false);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <Icon icon="mdi:close-circle" className="w-4 h-4" />
            </button>
          )}

          {open && (
            <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
              {isLoading ? (
                <div className="px-4 py-3 text-sm text-gray-500">
                  Loading devices...
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No devices found
                </div>
              ) : (
                filtered.map((d) => (
                  <button
                    key={d.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectDevice(d)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {d.name}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span className="font-mono">{d.id}</span>
                      {d.client?.name && <span>· {d.client.name}</span>}
                      {d.modelType && <span>· {d.modelType}</span>}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Enter or search a device ID — it fills the connection topic and, once a
          device is picked, its publish model below.
        </p>
        {selected?.client?.name && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <Icon icon="mdi:router-wireless" className="w-4 h-4 text-primary" />
            <span className="font-medium">{selected.name}</span>
            <span className="text-gray-500 dark:text-gray-400">
              · {selected.client.name}
            </span>
          </div>
        )}
      </div>

      {/* Config + publish appear only after a device is picked */}
      {selected && (
        <>
          {/* Connection settings for the selected device */}
          <div className="max-w-3xl">
            <ConfigurationWidget deviceId={selected.id} />
          </div>

          {/* Publish model for the selected device */}
          <div className="max-w-3xl">
            {paramsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : parameters.length > 0 ? (
              <MqttPublishWidget deviceId={selected.id} parameters={parameters} />
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-6 py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This device has no parameters configured, so there is no model
                  to publish.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function PlatformAdminPage() {
  const [activeTab, setActiveTab] = useState<Tab>("Users");
  const [search, setSearch] = useState("");

  return (
    <div className="flex-1 p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Platform Console
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            All users, organisations, locations and devices across the platform
          </p>
        </div>
        {activeTab !== "MQTT" && (
          <div className="relative w-full sm:w-72">
            <Icon
              icon="mdi:magnify"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${activeTab.toLowerCase()}...`}
              className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <Icon icon="mdi:close-circle" className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            <Icon icon={t.icon} className="w-4 h-4" />
            {t.key}
          </button>
        ))}
      </div>

      {activeTab === "Users" && <UsersTab search={search} />}
      {activeTab === "Organisations" && <OrganisationsTab search={search} />}
      {activeTab === "Locations" && <LocationsTab search={search} />}
      {activeTab === "Devices" && <DevicesTab search={search} />}
      {activeTab === "MQTT" && <MqttTab />}
    </div>
  );
}
